import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import Singer from "shared/assets/images/touring/tour.webp";
import BreakinSoundLogo from "shared/assets/images/touring/breaking-sound-logo.png";
import Airplane from "shared/assets/images/touring/airplane.svg";
import { ArtistState } from "explorer/reducers/artistas";
import { useSelector } from "react-redux";
import { openNewTab } from "shared/helpers/window-helpers";
import "./Touring.scss";
import { useEffect } from "react";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

export interface TouringProps {}

const Touring: React.FC = () => {
  const { t } = useTranslation();

  const { userArtist, loading } = useSelector(
    (state: ArtistState) => state.artists
  );
  const signed = userArtist?.breakingSoundForm?.alreadySubmited;

  const onClickCTA = () => {
    const link = userArtist?.breakingSoundForm?.formUrl;
    if (!link) return;
    openNewTab(link);
  };

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  // Elementos filhos - todos usam a mesma base
  const itemVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        type: "spring" as const,
        damping: 25,
        stiffness: 200,
      },
    },
  };

  // Variante especial para lista
  const listContainerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const listItemVariants = {
    initial: {
      opacity: 0,
      y: 30,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        type: "spring" as const,
        damping: 25,
        stiffness: 200,
      },
    },
  };

  // Avião com movimento especial
  const airplaneVariants = {
    initial: {
      y: -20,
      opacity: 0,
      rotate: -2,
    },
    animate: {
      y: 0,
      opacity: 1,
      rotate: 0,
      transition: {
        duration: 0.8,
        type: "tween" as const,
      },
    },
  };

  // Botão com bounce
  const buttonVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.34, 1.56, 0.64, 1] as [number, number, number, number],
      },
    },
  };

  const listItems = [
    t("pages.BreakingSound.ul.curetedShows"),
    t("pages.BreakingSound.ul.smartTools"),
    t("pages.BreakingSound.ul.support"),
  ];

  useEffect(() => {
    if (!loading) {
      if (signed) {
        dataLayerViewedEvent({
          ...tagManagerEvent.Touring.BREAKING_SOUND_SIGNED,
        });
      } else {
        dataLayerViewedEvent({
          ...tagManagerEvent.Touring.BREAKING_SOUND_UNSIGNED,
        });
      }
    }
  }, [signed, loading]);

  return (
    <section id="touring">
      <div className="container">
        <motion.div
          className="banner-gradient"
          style={
            { "--content-gap": signed ? "0px" : "44px" } as React.CSSProperties
          }
          variants={containerVariants}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.img
            src={Singer}
            alt="touring breaking sound"
            className="mask-gradient singer"
            variants={itemVariants}
          />

          <div className="banner-content">
            <motion.img
              src={BreakinSoundLogo}
              alt="touring breaking sound"
              className="logo"
              variants={itemVariants}
            />

            <motion.span variants={itemVariants}>
              <div
                className="airplane-h1"
                style={
                  {
                    "--margin-bottom-h1": signed ? "14px" : "0px",
                    "--margin-top-h1": signed ? "60px" : "20px",
                  } as React.CSSProperties
                }
              >
                <motion.div
                  className="airplane airplane-margin"
                  variants={airplaneVariants}
                >
                  <img
                    src={Airplane}
                    alt="touring breaking sound"
                    className="airplane"
                  />
                  <span>Tour</span>
                </motion.div>

                <motion.h1 variants={itemVariants}>
                  {signed
                    ? t("pages.BreakingSound.bannerMessageSigned")
                    : t("pages.BreakingSound.bannerMessageNotSigned")}
                </motion.h1>
              </div>

              <AnimatePresence>
                {!signed && (
                  <motion.span
                    className="button-wrapper"
                    variants={buttonVariants}
                    initial="initial"
                    animate="animate"
                    exit={{
                      opacity: 0,
                      scale: 0.9,
                      transition: { duration: 0.3 },
                    }}
                    onClick={onClickCTA}
                  >
                    <motion.button className="button" variants={buttonVariants}>
                      {t("pages.BreakingSound.buttonBanner")}
                    </motion.button>
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.span>
          </div>
        </motion.div>

        <motion.div
          className="content-container"
          variants={containerVariants}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.1, margin: "0px 0px -50px 0px" }}
        >
          <motion.h2 variants={itemVariants}>
            {t("pages.BreakingSound.howItWorks")}
          </motion.h2>

          <motion.ul
            variants={listContainerVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.1 }}
          >
            {listItems.map((item, index) => (
              <motion.li key={index} variants={listItemVariants}>
                <span className="check-circle" />
                {item}
              </motion.li>
            ))}
          </motion.ul>

          <motion.p variants={itemVariants}>
            {t("pages.BreakingSound.careerAssessment")}
          </motion.p>

          <motion.span
            style={{
              height: "1px",
              backgroundColor: "hsla(241, 100%, 90%, 0.2)",
              width: "100%",
              display: "block",
              margin: "20px 0px 40px 0px",
            }}
            variants={itemVariants}
          />

          <motion.h3
            variants={itemVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
          >
            {t("pages.BreakingSound.aboutBreakingSound")}
          </motion.h3>

          <motion.p
            variants={itemVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
          >
            {t("pages.BreakingSound.ourVenues")}
          </motion.p>

          <motion.p
            variants={itemVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
          >
            {t("pages.BreakingSound.industry")}
          </motion.p>

          <motion.span
            variants={itemVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
          >
            <>
              {t("pages.BreakingSound.question.text")}{" "}
              <a
                href="https://www.breakingsound.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("pages.BreakingSound.question.website")}{" "}
              </a>
              {t("pages.BreakingSound.question.or")}{" "}
              <a href="mailto:info@breakingsound.com">
                {t("pages.BreakingSound.question.contact")}
              </a>
            </>
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
};

export default Touring;
